/**
 * ReadonlyLabeledEdge — the edge used ONLY by the read-only graph preview
 * (PipelineGraphView). It draws the same smoothstep path as the editor's
 * default edges, but takes over label placement so branch chips don't sit on
 * top of the node a "skip" edge routes past.
 *
 * Just the "B" half of the fix: keep React Flow's default label POSITION (the
 * path midpoint — source-anchoring was tried and rejected: two edges off the
 * same fork stacked their labels on top of each other, worse than the midpoint
 * spread), but render the label as an opaque chip raised above node bodies (in
 * the CSS) so wherever it lands it stays legible instead of melting into the
 * icon/title behind it.
 *
 * Scoped to readonly: GraphFlowEditor only swaps labeled edges to this type
 * when `readonly` is set, so the editor's interactive "?" conditional edge is
 * untouched.
 *
 * The label POSITION is still React Flow's, and that is deliberate — see above.
 * What the "A" half became instead is the SOURCE PORT fan: sibling branches get
 * separated where they LEAVE (`utils/siblingFan.js`), which spreads their
 * midpoints as a consequence, instead of moving the chips themselves.
 */

import React from 'react';
import { EdgeLabelRenderer, BaseEdge, getSmoothStepPath, Position } from 'reactflow';
import { SAT_DY } from '../utils/memberSatellites';
import { sourceFanOf } from '../utils/siblingFan';
import { FibreBeams, FIBRE_DIM_STROKE } from './FibreBeams';
import './ReadonlyLabeledEdge.css';

// A BACK-EDGE is one whose target sits to the LEFT of its source — a loop, like
// a retry routing back to an earlier node. Nodes flow left-to-right, so React
// Flow can't take a direct route and wraps the line around instead.
export const isBackEdge = (sourceX, targetX) => targetX < sourceX;

// ── A NODE'S ATTACHMENT LANE IS PART OF ITS FOOTPRINT ───────────────────────
// The strip directly under a node's icon box, where its model disc / member
// satellites and their captions live. Nothing else belongs in it — not a routed
// edge, and not the label chip React Flow parks on that edge's turn.
//
// Measured DOWN FROM the `bottom` handle these edges leave from, because that is
// what `offset` is relative to. The handle is pinned to the icon box's underside
// (WorkflowNode.css / DecisionNode.css `top: 83px`, 10px tall) and React Flow
// takes a Bottom handle's y as its LOWER edge, so the handle sits 93 below the
// node's top:
//     SAT_DY            satellite top   (= modelSatellites' DISC_DY)
//   + SAT_CAPTION_TOP   its caption     (MemberNode.css / ModelNode.css `top: 46px`)
//   + SAT_CAPTION_H     two lines of it (10px / 1.25 — names DO wrap at 110px)
//   - BOTTOM_HANDLE_Y   back to the handle the offset is measured from
//   + CHIP_HALF + AIR   so the chip clears the lane, not just its own centre
// A tripwire test asserts these against the stylesheets they were read from.
const SAT_CAPTION_TOP = 46;
const SAT_CAPTION_H = 25;
const BOTTOM_HANDLE_Y = 93;
const CHIP_HALF = 11;   // 11px/1.3 text + 2px padding + 1px border, halved
const AIR = 4;
export const ATTACHMENT_LANE =
  SAT_DY + SAT_CAPTION_TOP + SAT_CAPTION_H - BOTTOM_HANDLE_Y + CHIP_HALF + AIR;

// Which edges TURN inside that lane — the only ones that need standing off.
// `offset` is how far a smoothstep path runs from its handle before turning,
// and for exactly these two shapes the turn is also where React Flow puts the
// midpoint label: a BACK-EDGE (which we deliberately route along the lane), and
// a BOTTOM EXIT whose target is not below it (a skip bowing under the row, an
// exit to a level or earlier node).
//
// Two shapes fall out untouched, with no second gate: a descending branch turns
// down at its target's row, far below the lane; and a satellite's OWN connector
// is supposed to land in the lane — its target hangs below the bottom handle,
// so `targetY <= sourceY` excludes it.
export const turnsInAttachmentLane = ({ back, sourcePosition, sourceY, targetY }) =>
  back || (sourcePosition === Position.Bottom && targetY <= sourceY);

export const ReadonlyLabeledEdge = ({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  style = {},
  markerEnd,
  label,
  data,
}) => {
  // Same path geometry as the editor's default 'smoothstep' edges so the line
  // itself looks identical. labelX/labelY are React Flow's own midpoint — the
  // original ("old") label position the user preferred; we keep it and only
  // restyle the chip.
  // Route BACK-EDGES along the BOTTOM. Left-to-right handles (source Right,
  // target Left) leave a loop no direct path, so smoothstep wraps it OVER the
  // top — and the detour runs across whatever nodes sit between, so a `retry`
  // hopping one node back reads as if it returns to the very start of the graph.
  // Telling smoothstep the loop leaves and re-enters from BELOW sends it under
  // the row instead, where the lane is empty: the line stays visibly tied to the
  // two nodes it actually connects. Forward edges are untouched.
  //
  // ONE BRANCH, ONE PORT. Sibling branches leaving the same handle would depart
  // from the identical point and run on top of each other; the layouter resolves
  // a per-branch offset ALONG that handle's edge of the node (`siblingFan`) and
  // this is where it is applied. Only the SOURCE end moves — the arrowhead still
  // lands on the target's handle — and the offset is bounded by the node's own
  // flat half-span, so the port stays ON the box with no gap under the line.
  //
  // The two ROUTING questions are answered from the UNSHIFTED endpoints on
  // purpose: `back` compares xs and `turnsInAttachmentLane` compares ys, so
  // asking them after the shift would let a port offset re-classify a forward
  // branch as a loop (or vice versa) and swing the whole path. An edge the
  // layouter did not fan reads zeros, so nothing below changes for it.
  const { dx, dy } = sourceFanOf(data);
  const back = isBackEdge(sourceX, targetX);
  const standOff = turnsInAttachmentLane({ back, sourcePosition, sourceY, targetY });
  const [edgePath, labelX, labelY] = getSmoothStepPath({
    sourceX: sourceX + dx,
    sourceY: sourceY + dy,
    sourcePosition: back ? Position.Bottom : sourcePosition,
    targetX,
    targetY,
    targetPosition: back ? Position.Bottom : targetPosition,
    borderRadius: 8,
    // Clear the node bodies AND their attachment lane before turning, so the
    // horizontal run — and the label chip React Flow puts on that turn — sits in
    // the gutter BELOW the discs/satellites instead of across their icons. (Was
    // `back ? 28`, which put both squarely inside the lane: on a node carrying a
    // satellite, a retry label landed on the satellite's tile and the line ran
    // through it.)
    ...(standOff ? { offset: ATTACHMENT_LANE } : {}),
  });

  // LIGHT IN THE FIBRE. A member connector carries the member's run count
  // (memberSatellites stamps `data.activeRuns` — the same number its paper
  // stack is drawn from). While it is > 0 the static line dims to a blue tint
  // and FibreBeams runs a pulse down the SAME path, parent → member, faster
  // the more runs. Every other edge reads 0 here and is untouched.
  const activeRuns = Math.max(0, Number(data?.activeRuns) || 0);
  const lit = activeRuns > 0;

  return (
    <>
      <BaseEdge
        id={id}
        path={edgePath}
        markerEnd={markerEnd}
        style={lit ? { ...style, stroke: FIBRE_DIM_STROKE } : style}
      />
      {lit && <FibreBeams path={edgePath} activeRuns={activeRuns} />}
      {label && (
        <EdgeLabelRenderer>
          <div
            className="readonly-edge-label"
            style={{
              transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)`,
            }}
          >
            {label}
          </div>
        </EdgeLabelRenderer>
      )}
    </>
  );
};

export const ReadonlyLabeledEdgeType = ReadonlyLabeledEdge;
export default ReadonlyLabeledEdge;
